import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { userApi } from '../../services/api';
import { User, UserCreateRequest, UserUpdateRequest } from '../../types';
import UserList from './UserList';
import UserForm from './UserForm';
import {
  PlusIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import ErrorAlert from '../common/ErrorAlert';

type ViewMode = 'list' | 'create' | 'edit';

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const itemsPerPage = 20;

  const fetchUsers = async (page: number = 1) => {
    try {
      // Only show loading on first load, not on page changes
      if (users.length === 0) {
        setLoading(true);
      }
      const skip = (page - 1) * itemsPerPage;
      const response = await userApi.getAllUsers(skip, itemsPerPage, 'created_at', 'desc');
      setUsers(response.items);
      setTotalUsers(response.total);
      setCurrentPage(page);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to fetch users'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="max-w-7xl mx-auto">
        <ErrorAlert
          message="You need admin privileges to access user management."
          type="info"
        />
      </div>
    );
  }

  const handleCreateUser = async (userData: UserCreateRequest) => {
    try {
      await userApi.createUser(userData);
      setMessage({ type: 'success', text: 'User created successfully!' });
      setViewMode('list');
      fetchUsers();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to create user'
      });
    }
  };

  const handleUpdateUser = async (id: number, userData: UserUpdateRequest) => {
    try {
      await userApi.updateUser(id, userData);
      setMessage({ type: 'success', text: 'User updated successfully!' });
      setViewMode('list');
      setSelectedUser(null);
      fetchUsers();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to update user'
      });
    }
  };

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      if (isActive) {
        await userApi.deactivateUser(id);
        setMessage({ type: 'success', text: 'User deactivated successfully!' });
      } else {
        await userApi.activateUser(id);
        setMessage({ type: 'success', text: 'User activated successfully!' });
      }
      fetchUsers();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to update user status'
      });
    }
  };

  const handleDeleteUser = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await userApi.deleteUser(id);
        setMessage({ type: 'success', text: 'User deleted successfully!' });
        fetchUsers();
      } catch (error: any) {
        setMessage({
          type: 'error',
          text: error.response?.data?.detail || 'Failed to delete user'
        });
      }
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setViewMode('edit');
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {viewMode !== 'list' && (
              <button
                onClick={() => {
                  setViewMode('list');
                  setSelectedUser(null);
                }}
                className="mr-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back
              </button>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {viewMode === 'list' && 'User Management'}
                {viewMode === 'create' && 'Create New User'}
                {viewMode === 'edit' && 'Edit User'}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                {viewMode === 'list' && 'Manage user accounts and permissions'}
                {viewMode === 'create' && 'Create a new user account'}
                {viewMode === 'edit' && 'Update user information and permissions'}
              </p>
            </div>
          </div>
          
          {viewMode === 'list' && (
            <button
              onClick={() => setViewMode('create')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create User
            </button>
          )}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className="mb-6">
          <ErrorAlert
            message={message.text}
            type={message.type === 'success' ? 'success' : 'error'}
          />
        </div>
      )}

      {/* Content */}
      {viewMode === 'list' && (
        <UserList
          users={users}
          loading={loading}
          currentPage={currentPage}
          totalPages={Math.ceil(totalUsers / itemsPerPage)}
          totalUsers={totalUsers}
          itemsPerPage={itemsPerPage}
          onPageChange={fetchUsers}
          onEdit={handleEditUser}
          onToggleActive={handleToggleActive}
          onDelete={handleDeleteUser}
        />
      )}

      {viewMode === 'create' && (
        <UserForm
          mode="create"
          onSubmit={handleCreateUser}
          onCancel={() => setViewMode('list')}
        />
      )}

      {viewMode === 'edit' && selectedUser && (
        <UserForm
          mode="edit"
          user={selectedUser}
          onSubmit={(userData) => handleUpdateUser(selectedUser.id, userData)}
          onCancel={() => {
            setViewMode('list');
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;